import { observable, action } from "mobx"
import axios from "axios"
import { v4 as uuidv4 } from 'uuid'
import { getCsrfToken } from "../utils/userUtils"
import { logEvent } from "../utils/analytics"

export default class Sequence {
  @observable name
  @observable data
  @observable id
  @observable uuid
  @observable audio
  @observable isPlaying
  @observable enabledForPlay
  @observable isFavorited

  constructor(sequenceObject = {}) {
    const o = sequenceObject
    this.csrfToken = getCsrfToken()
    this.id = o.id
    this.uuid = o.uuid || uuidv4()
    this.name = o.name || ''
    this.data = o.data || {
      sequenceData: {},
      trackLengths: {},
      playbackRates: {},
      volumeLevels: {},
      maxPlayDurations: {},
      reverbMix: 0.5,
      tempo: 120,
      humanizationAmount: 0.02,
      swingAmount: 0,
      sampleKeys: {}
    }
    this.audio = o.audio || null
    this.audioWaveformUrl = o.audioWaveformUrl || null
    this.audioMp3Url = o.audioMp3Url || null
    this.assigned_color = "#64b5f6"
    this.enabledForPlay = true
    this.favorites_count = o.favorites_count || 0
    this.isPlaying = false
    this.isFavorited = false
    this.sequence_url = o.sequence_url
    this.tag_list = o.tag_list
    this.tempo = o.tempo || 120
    this.duration = Number.parseFloat(o.duration) || null
    this.slug = o.slug
    this.user_display_name_truncated = o.user_display_name_truncated
    this.created_at = o.created_at ? new Date(o.created_at) : new Date()
    this.updated_at = o.updated_at ? new Date(o.updated_at) : new Date()
  }

  @action
  setName = (name) => {
    this.name = name
  }

  @action
  setData = (data) => {
    this.data = data
  }

  @action
  setAudio = (audio) => {
    this.audio = audio
  }

  @action
  setToPlay = () => {
    this.isPlaying = true
  }

  @action
  setToStop = () => {
    this.isPlaying = false
  }

  @action
  setToFavorite = () => {
    this.isFavorited = true
  }

  async save() {
    if (!this.name.trim()) {
      throw new Error('Sequence name is required')
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: this.id ? "patch" : "post",
        url: this.id ? `/sequences/${this.id}` : "/sequences",
        timeout: 25000,
        data: {
          sequence: {
            name: this.name,
            data: this.data,
            uuid: this.uuid,
            audio: this.audio
          }
        }
      })

      // Update the instance with the response data
      if (response.data.sequence.id) {
        this.id = response.data.sequence.id
      }

      return true
    } catch (error) {
      console.error("Save sequence error:", error)
      throw error
    }
  }

  async delete() {
    if (!this.id) {
      throw new Error('Cannot delete unsaved sequence')
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      await axios({
        method: "delete",
        url: `/sequences/${this.id}`,
        timeout: 25000
      })
      return true
    } catch (error) {
      console.error("Delete sequence error:", error)
      throw error
    }
  }

  createPlay = async () => {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "post",
        url: "/plays",
        timeout: 5000,
        data: {
          sequence_id: this.id,
          authenticity_token: this.csrfToken
        }
      })
      return true
    } catch (error) {
      console.error("create play error", error)
      return false
    }
  }

  async favorite() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "post",
        url: "/favorites",
        timeout: 25000,
        data: {
          favoritable_id: this.id,
          favoritable_type: 'Sequence',
          authenticity_token: this.csrfToken,
        },
      })

      logEvent("Favorited sequence")
      this.favorites_count += 1
      this.isFavorited = true
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async unfavorite() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "delete",
        url: "/favorites/" + this.id,
        timeout: 25000,
        data: {
          favoritable_type: 'Sequence',
          authenticity_token: this.csrfToken,
        },
      })

      this.favorites_count -= 1
      this.isFavorited = false
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  toJSON() {
    return {
      id: this.id,
      uuid: this.uuid,
      name: this.name,
      data: this.data,
      created_at: this.created_at,
      updated_at: this.updated_at
    }
  }
}